import styled from 'styled-components'
import NumberFormat from 'react-number-format'
import { useSelector } from 'react-redux'

import { palette, mediaQueries, sizes, fonts } from '../../styles/variables'
import { PrimaryButtonElement } from '../buttons/Buttons'
import { getLocale } from '../../utils/locations'
import { translate } from '../../utils/translation'
import { selectCachedLocations } from '../../state/selectors/locationManager'

import DropDownList from './DropdownList'

// downArrow
const Button = styled.span(() => ({
  padding: '15px 20px',
  backgroundColor: 'transparent',
  border: `1px solid ${palette.lightGray50}`,
  color: palette.darkGray50,
  cursor: 'pointer',
  fontSize: '15px',
}))

const SearchInput = styled.input.attrs({
  autoComplete: 'do-not-autofill',
})({
  height: '39px',
  minHeight: '39px',
  border: `1px solid ${palette.lightGray50}`,
  borderRadius: '2px',
  fontSize: '14px',
  color: palette.darkGray50,
  fontFamily: 'geomanistregular',
  padding: '0 20px',
  flex: 1,
  '::placeholder': {
    color: palette.darkGray50,
  },
  [mediaQueries]: {
    padding: '0 10px',
  },
})

const getMobileBackgroundColor = ({ disabled, theme }) => {
  if (disabled) {
    return palette.lightGray50
  }

  return theme === 'orange' ? palette.orange : palette.white
}

const FilterButton = styled(PrimaryButtonElement)((props) => ({
  maxWidth: '196px',
  flex: 1,
  height: sizes.buttonUnit,
  lineHeight: sizes.buttonUnit,
  cursor: props.disabled ? 'not-allowed' : 'pointer',
  pointerEvents: 'all',
  fontFamily: fonts.primary,
  fontWeight: 'bold',
  letterSpacing: '1px',
  [mediaQueries.mobile]: {
    backgroundColor: getMobileBackgroundColor(props),
    color: props.theme === 'orange' ? palette.white : palette.orange,
    width: 'auto',
  },
}))

// Narrow filter
const ButtonNarrowFilterContent = styled.button((props) => ({
  border: `1px solid ${palette.lightGray50}`,
  borderRadius: '2px',
  backgroundColor: props.color === 'orange' ? palette.orange : palette.white,
  fontSize: '12px',
  // width: '162px',
  height: sizes.buttonUnit,
  lineHeight: sizes.buttonUnit,
  padding: '0 15px',
  cursor: 'pointer',
  color: props.color === 'orange' ? palette.white : palette.darkGray50,
  fontFamily: fonts.primaryMedium,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  '> div': {
    lineHeight: '13px',
  },
}))

const ButtonNarrowFilter = ({ onClick, color, tag }) => (
  <ButtonNarrowFilterContent
    onClick={onClick}
    color={color}
    data-testid={tag.replace(/[^\w]/g, '').toLowerCase()}
  >
    {tag}
  </ButtonNarrowFilterContent>
)

const TableFields = styled.form({
  display: 'flex',
  flexWrap: 'wrap',
  padding: '20px',
  border: `1px solid ${palette.lightGray50}`,
  [mediaQueries.mobile]: {
    padding: '0px',
    border: 'none',
  },
})

const TableFieldLeft = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  flexGrow: '1',
  flexShrink: '1',
  flexBasis: 'auto',
  padding: '0 20px',
  [mediaQueries.mobile]: {
    padding: '0px',
    width: '100%',
  },
})

const TableFieldRight = styled.div((props) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  flexGrow: '1',
  flexShrink: '1',
  flexBasis: 'auto',
  padding: '0 20px',
  [mediaQueries.mobile]: {
    width: '100%',
    padding: props.mobilePadding || '0px',
    marginLeft: 0,
  },
}))

const fieldUnit = sizes.buttonUnit
const textareaUnit = (parseInt(fieldUnit, 10) * 3).toString()

const FieldLabel = styled.div({
  display: 'flex',
  alignItems: 'flex-end',
  padding: '0 0 10px 0',
  height: fieldUnit,
  fontSize: '14px',
  lineHeight: '14px',
  fontFamily: fonts.primaryBook,
})

const FieldNumberInput = styled(NumberFormat)({
  flex: 1,
  width: '50%',
  height: fieldUnit,
  border: `1px solid${palette.lightGray50}`,
  padding: '0 0 0 15px',
  borderRadius: 0,
  WebkitAppearance: 'none',
  ':first-child': {
    margin: '0 10px 0 0',
  },
  '::placeholder': {
    color: palette.lightGray50,
  },
})

const TextareaField = styled.textarea({
  height: `${textareaUnit}px`,
  resize: 'none',
  border: `1px solid${palette.lightGray50}`,
  padding: '15px',
  '::placeholder': {
    color: palette.lightGray50,
  },
})

const FieldWrapper = styled.div({
  padding: '0',
})

const FieldInputWrapper = styled.div({
  display: 'flex',
})

const SubmitWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  height: fieldUnit,
  margin: `${fieldUnit} 0 0 0`,
  [mediaQueries.mobile]: {
    margin: '15px 0 0 0',
  },
})

const Row = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  [mediaQueries.mobile]: {
    flexDirection: 'column',
  },
})

const Title = styled.span({
  color: palette.darkGray,
  fontSize: '16px',
  fontWeight: 500,
  textAlign: 'center',
  [mediaQueries.mobile]: {
    marginLeft: 0,
    marginTop: 20,
  },
})

const SingleFilter = ({
  lang,
  locId,
  title,
  min,
  max,
  onChangeFilters,
  name,
}) => {
  const cachedLocations = useSelector(selectCachedLocations())
  const { thousandSeparator, decimalSeparator } = getLocale(
    lang,
    locId,
    cachedLocations,
  )

  return (
    <FieldWrapper>
      <FieldLabel>{title}</FieldLabel>
      <FieldInputWrapper>
        <FieldNumberInput
          data-testid="min-field-number-input"
          placeholder={translate('min')}
          type="text"
          value={min}
          onValueChange={(e) => {
            onChangeFilters(name, 'min', e.floatValue)
          }}
          thousandSeparator={thousandSeparator}
          decimalSeparator={decimalSeparator}
        />
        <FieldNumberInput
          data-testid="max-field-number-input"
          placeholder={translate('max')}
          type="text"
          value={max}
          onValueChange={(e) => {
            onChangeFilters(name, 'max', e.floatValue)
          }}
          thousandSeparator={thousandSeparator}
          decimalSeparator={decimalSeparator}
        />
      </FieldInputWrapper>
    </FieldWrapper>
  )
}

const FilterField = ({ type, title, options, changeValue, value, onClick }) => {
  if (type === 'textfield') {
    return (
      <SearchInput
        data-testid={title.replace(' ', '').toLowerCase()}
        placeholder={title}
        value={value}
        onChange={(val) => changeValue(val)}
      />
    )
  }
  if (type === 'button') {
    return (
      <Button
        onClick={onClick}
        data-testid={title.replace(/\s+/g, '').replace(/\+/g, '').toLowerCase()}
      >
        {title}
      </Button>
    )
  }
  return (
    <DropDownList
      title={title}
      searchable
      options={options}
      onSelectItem={changeValue}
    />
  )
}

const FilterValue = styled.div({
  color: palette.darkGray,
  height: '48px',
  padding: '16px',
  fontSize: '14px',
  fontFamily: fonts.primaryBook,
  ':hover': {
    background: palette.lightGray20,
  },
})

const FilterRange = styled.div({
  display: 'flex',
  padding: '16px',
  justifyContent: 'space-between',
  cursor: 'default',
})

const ColorDot = styled.div((props) => ({
  width: '12px',
  height: '12px',
  borderRadius: '6px',
  background: props.color,
}))

const Value = styled.div({
  marginLeft: '12px',
})

export {
  FilterField,
  FilterButton,
  ButtonNarrowFilter,
  TableFields,
  TableFieldLeft,
  TableFieldRight,
  FieldLabel,
  FieldWrapper,
  TextareaField,
  SubmitWrapper,
  Row,
  Title,
  SingleFilter,
  FilterValue,
  FieldNumberInput,
  FilterRange,
  ColorDot,
  Value,
}
